import { atom, selector, useRecoilState, useRecoilValue, useRecoilValueLoadable } from 'recoil'
import { CatalogDigest, CatalogItem, CatalogSection } from '../types/catalogData'
import { logger } from "../shared/logger";
import { ServiceablePartType } from '../types/serviceability';
import { getConfiguration } from '../vertex/vertexConfig';

/**
 * Catalog ID
 */
export const catalogIdAtom = atom<string | null | undefined>({
  key: 'catalog.catalogId',
  default: null,
});
export const useCatalogId = () => useRecoilState(catalogIdAtom);

/**
 * Catalog Digest
 */
export const catalogDigest = selector<CatalogDigest>({
  key: 'catalog.catalogDigest',
  get: async ({ get }) => {
    const catalogId = get(catalogIdAtom);
    logger.log(`Loading catalog digest for catalog: ${catalogId}`);
    let response: CatalogDigest = {
      catalogId : catalogId ?? '',
      sceneId: '',
      sceneViewStateId: '',
      cacheKey: '',
      rootSection: {
        partName: '',
        description: '',
        partType: ServiceablePartType.SECTION,
        thumbnail: null,
        quantity: 0,
        sectionId : '',
        catalogId: catalogId ?? '',
        sceneId: '',
        sceneViewStateId: '',
        partNumber: null,
        sceneItems: [],
        size: 0,
        items: [],
      }
    };
    if (catalogId != null && catalogId.length > 0) {
      try {
        response = await getCatalogDigest(catalogId);
      } catch (e) {
        logger.log(e);
      }
    }
    return response;
  },
});
export const useCatalogDigest = () => useRecoilValueLoadable<CatalogDigest>(catalogDigest);

/**
 * Catalog Section ID
 */
export const catalogSectionIdAtom = atom<string | null | undefined>({
  key: 'catalog.catalogSectionId',
  default: null,
});
export const useCatalogSectionId = () => useRecoilState(catalogSectionIdAtom);

/**
 * Catalog Section
 */
export const catalogSection = selector<CatalogSection>({
  key: 'catalog.catalogSection',
  get: async ({ get }) => {
    const catalog = get(catalogDigest);
    let sectionId = get(catalogSectionIdAtom);
    if (sectionId == null && catalog.rootSection != null) {
      sectionId = catalog.rootSection.sectionId;
    }
    logger.log(`Loading catalog section: ${sectionId}`);
    let response: CatalogSection = catalog.rootSection;
    if (sectionId != null && sectionId.length > 0) {
      try {
        response = await getCatalogSection(sectionId, catalog.cacheKey);
      } catch (e) {
        logger.log(e);
      }
    }
    return response;
  },
});
export const useCatalogSection = () => useRecoilValueLoadable<CatalogSection>(catalogSection);

/**
 * Selected catalog items
 */
export const selectedCatalogItems = atom<CatalogItem[] | null>({
  key: 'catalog.selectedCatalogItems',
  default: null,
});
export const useSelectedCatalogItems = () => useRecoilState(selectedCatalogItems);

/**
 * Previous selection from viewer?
 */
export const previousSelectionFromViewer = atom<boolean | null>({
  key: 'catalog.previousSelectionFromViewer',
  default: false,
});
export const usePreviousSelectionFromViewer = () => useRecoilState(previousSelectionFromViewer);

/** Catalog Item filter string */
export const catalogItemFilter = atom<string>({
  key: 'catalog.catalogItemFilter',
  default: "",
});
export const useCatalogItemFilter = () => useRecoilState(catalogItemFilter);

/**
 * Filtered catalog items
 */
export const filteredCatalogItems = selector<Array<CatalogItem|CatalogSection>>({
  key: 'catalog.filteredCatalogItems',
  get: ({ get }) => {
    const filterString = get(catalogItemFilter);
    const section = get(catalogSection);
    const allItems = section != null ? section.items : [];
    return filterString == null || filterString.length < 2
      ? allItems
      : allItems?.filter((item) => item.partName.toLocaleLowerCase().includes(filterString.toLocaleLowerCase()) || item.partNumber?.toLocaleLowerCase().includes(filterString.toLocaleLowerCase()));
  },
});
export const useFilteredCatalogItems = () => useRecoilValueLoadable(filteredCatalogItems);

export const nonfilteredCatalogItems = selector<Array<CatalogItem|CatalogSection>>({
  key: 'catalog.nonfilteredCatalogItems',
  get: ({ get }) => {
    const filterString = get(catalogItemFilter);
    // const section = get(catalogSection);
    // const allItems = section != null ? section.items : [];
    const allItems:CatalogItem[] = [];
    return filterString == null || filterString.length < 2
      ? []
      : allItems?.filter((item) => !item.partName.toLocaleLowerCase().includes(filterString.toLocaleLowerCase()) && !item.partNumber?.toLocaleLowerCase().includes(filterString.toLocaleLowerCase()));
  },
});
export const useNonfilteredCatalogItems = () => useRecoilValueLoadable(nonfilteredCatalogItems);

const getCatalogDigest = async (catalogId: string): Promise<CatalogDigest> => {
  const resp = await fetch(`api/catalog-digest?catalogId=${catalogId}`, {
    method: 'GET'
  });
  if (resp.status === 200) {
    const catalogDigest: CatalogDigest = await resp.json();
    return catalogDigest;
  } else {
    throw new Error(`Unable to load catalog digest. status:${resp.status}`);
  }
}

const getCatalogSection = async (sectionId: string, cacheKey:string): Promise<CatalogSection> => {
  const resp = await fetch(`api/catalog-section?sectionId=${sectionId}&cacheKey=${cacheKey}`, {
    method: 'GET'
  });
  if (resp.status === 200) {
    const catalogSection: CatalogSection = await resp.json();
    return catalogSection;
  } else {
    throw new Error(`Unable to load catalog section. status:${resp.status}`);
  }
}

/**
 * Show service documents panel?
 */
const showServiceDocuments = atom<boolean>({
  key: 'catalog.showServiceDocuments',
  default: false,
});
export const useShowServiceDocuments = () => useRecoilState(showServiceDocuments);